import { useContext } from 'react'
import { Button } from 'app/components/ui/button'
import { Input } from 'app/components/ui/input'
import { PageHeader } from 'app/components/ui/page-header'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'app/components/ui/table'
import type { CacheSearchResult, UseOpsCacheReturn } from './useOpsCache'
import { PAGE_GUTTER, PageGutterContext } from 'app/components/layout/PageScroll'
import { cn } from 'app/lib/utils'

export type OpsCacheShadcnComponentProps = UseOpsCacheReturn

export function OpsCacheShadcnComponent({
  connected,
  connError,
  searchPattern,
  setSearchPattern,
  deletePattern,
  setDeletePattern,
  showDeleteConfirm,
  setShowDeleteConfirm,
  isSearching,
  isDeleting,
  searchData,
  deleteData,
  onSearch,
  onDelete,
}: OpsCacheShadcnComponentProps) {
  const gutter = useContext(PageGutterContext)
  return (
    <div className={cn(`flex h-full flex-col pt-8`, PAGE_GUTTER[gutter])}>
      <PageHeader title="Redis Cache Management" />
      <div className="mb-6">
        {connected ? (
          <span className="inline-flex items-center rounded-full bg-green-100 px-3 py-1 text-sm text-green-700">
            <span className="mr-2 h-2 w-2 rounded-full bg-green-500" />
            Connected
          </span>
        ) : (
          <span className="inline-flex items-center rounded-full bg-red-100 px-3 py-1 text-sm text-red-700">
            <span className="mr-2 h-2 w-2 rounded-full bg-red-500" />
            Disconnected: {connError}
          </span>
        )}
      </div>

      {/* Search Section */}
      <div className="mb-8 rounded-lg border bg-surface p-4 shadow-sm">
        <h2 className="mb-4 text-lg font-semibold">Search Keys</h2>
        <p className="mb-4 text-sm text-muted-foreground">
          Search for Redis keys using a pattern. Use * as wildcard (e.g.,{` `}
          <code className="bg-surface-subtle px-1">session:*</code>,{` `}
          <code className="bg-surface-subtle px-1">Organization-*</code>).
        </p>
        <div className="flex gap-3">
          <Input
            value={searchPattern}
            onChange={(e) => setSearchPattern(e.target.value)}
            placeholder="Enter pattern... (e.g., session:* or *user*)"
            className="flex-1"
            onKeyDown={(e) => {
              if (e.key === `Enter`) onSearch()
            }}
          />
          <Button onClick={onSearch} disabled={isSearching || !searchPattern.trim() || !connected}>
            {isSearching ? `Searching...` : `Search`}
          </Button>
        </div>

        {searchData && (
          <div className="mt-4">
            {searchData.error ? (
              <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">{searchData.error}</div>
            ) : (
              <div>
                <div className="mb-2 text-sm text-muted-foreground">
                  Found {searchData.totalKeys} key(s)
                  {searchData.truncated && ` (showing first 100)`}
                </div>
                {searchData.results?.length > 0 ? (
                  <Table containerClassName="max-h-96 overflow-auto">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Key</TableHead>
                        <TableHead className="w-24">TTL</TableHead>
                        <TableHead>Value (preview)</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {searchData.results.map((r: CacheSearchResult) => (
                        <TableRow key={r.key}>
                          <TableCell className="whitespace-nowrap font-mono text-xs">{r.key}</TableCell>
                          <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                            {r.ttl === -1 ? `No expiry` : r.ttl === -2 ? `Expired` : `${r.ttl}s`}
                          </TableCell>
                          <TableCell
                            className="max-w-md truncate font-mono text-xs text-muted-foreground"
                            title={r.value}
                          >
                            {r.value.slice(0, 200)}
                            {r.value.length > 200 && `...`}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="rounded-md bg-surface-subtle p-3 text-sm text-muted-foreground">No keys found</div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Delete Section */}
      <div className="rounded-lg border border-red-200 bg-surface p-4 shadow-sm">
        <h2 className="mb-4 text-lg font-semibold text-red-700">Delete Keys</h2>
        <p className="mb-4 text-sm text-muted-foreground">
          Delete Redis keys matching a pattern. <strong className="text-red-600">This action cannot be undone.</strong>
        </p>
        <div className="flex gap-3">
          <Input
            value={deletePattern}
            onChange={(e) => setDeletePattern(e.target.value)}
            placeholder="Enter pattern to delete... (e.g., cache:temp:*)"
            className="flex-1 border-red-200"
          />
          <Button
            variant="danger"
            onClick={() => setShowDeleteConfirm(true)}
            disabled={isDeleting || !deletePattern.trim() || !connected}
          >
            Delete Keys
          </Button>
        </div>

        {showDeleteConfirm && (
          <div className="mt-4 rounded-md bg-red-50 p-4">
            <p className="mb-3 text-sm text-red-700">
              Are you sure you want to delete all keys matching <code className="bg-red-100 px-1">{deletePattern}</code>
              ?
            </p>
            <div className="flex gap-2">
              <Button variant="danger" size="sm" onClick={onDelete} disabled={isDeleting}>
                {isDeleting ? `Deleting...` : `Confirm Delete`}
              </Button>
              <Button variant="white" size="sm" onClick={() => setShowDeleteConfirm(false)}>
                Cancel
              </Button>
            </div>
          </div>
        )}

        {deleteData && (
          <div className={`mt-4 rounded-md p-3 text-sm ${deleteData.success ? `bg-green-50` : `bg-red-50`}`}>
            {deleteData.success ? (
              <span className="text-green-700">{deleteData.message}</span>
            ) : (
              <span className="text-red-700">{deleteData.error}</span>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
